import React, { useState, useEffect } from 'react';
import { FaWindows, FaApple, FaLinux } from 'react-icons/fa';
import { teacherApi } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

// react-icons' `IconType` return type is `ReactNode` in this version, which
// React 19's stricter JSX typing rejects when used as a component. Cast to a
// concrete component type so the icon elements render cleanly.
type IconCmp = React.FC<{ size?: number; color?: string }>;
const PLATFORMS: { name: string; Icon: IconCmp }[] = [
  { name: 'Pardus', Icon: FaLinux as IconCmp },
  { name: 'Windows', Icon: FaWindows as IconCmp },
  { name: 'Mac OS', Icon: FaApple as IconCmp },
];

const TeacherSmartBoard: React.FC = () => {
  const [copyToast, setCopyToast] = useState(false);
  const [boardCode, setBoardCode] = useState<string>('—');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    teacherApi.smartBoard()
      .then((res) => {
        const data = res.data;
        const first = Array.isArray(data) ? data[0] : data;
        const code = first?.room_code ?? first?.code ?? first?.id ?? null;
        if (code) setBoardCode(String(code));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = () => {
    if (boardCode === '—') return;
    navigator.clipboard.writeText(boardCode).catch(() => {});
    setCopyToast(true);
  };

  useEffect(() => {
    if (!copyToast) return;
    const t = setTimeout(() => setCopyToast(false), 2000);
    return () => clearTimeout(t);
  }, [copyToast]);

  return (
    <div>
      {copyToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#3973bf] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] shadow-lg animate-pulse">
          Kod Kopyalandı!
        </div>
      )}

      {/* Main card */}
      <div
        className="bg-[#fafafa] rounded-[24px] p-0"
        style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)', height: 634 }}
      >
        <div className="flex h-full">
          {/* LEFT SECTION */}
          <div className="flex-1 flex flex-col justify-center px-[128px]">
            {/* Title */}
            <p className="text-center mb-6" style={{ ...F, fontSize: 32, fontWeight: 500, color: '#3973bf' }}>
              Akıllı Tahta Kodu
            </p>

            {/* Description */}
            <p className="mb-5" style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.5' }}>
              Kitap kodlarınıza aşağıdan erişebilirsiniz, kodu kopyalayarak ... uygulamasına yapıştırın
            </p>

            {/* Code field with Kodu Kopyala button overlapping right side */}
            <div className="relative" style={{ width: 496, height: 69 }}>
              <div
                className="absolute inset-0 bg-white flex items-center px-5"
                style={{ borderRadius: 17 }}
              >
                <span style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{loading ? 'Yükleniyor…' : boardCode}</span>
              </div>
              {/* Kodu Kopyala button — overlapping right side of code field */}
              <button
                onClick={handleCopy}
                className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center hover:brightness-95 transition-all"
                style={{
                  width: 208, height: 49, borderRadius: 17,
                  background: '#fafafa', border: '2.8px solid #3973bf',
                  boxShadow: '0px 1.4px 5.6px rgba(0,0,0,0.12)',
                }}
              >
                <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#3973bf' }}>Kodu Kopyala</span>
              </button>
            </div>
          </div>

          {/* RIGHT SECTION */}
          <div className="flex-1 flex flex-col justify-center px-[60px]">
            {/* Description */}
            <p className="mb-6" style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b', lineHeight: '1.5', maxWidth: 496 }}>
              Akıllı tahta uygulamasını kullanmak istediğiniz platforma göre butonlara basarak indirebilirsiniz
            </p>

            {/* Platform buttons */}
            <div className="space-y-[23px]" style={{ maxWidth: 496 }}>
              {PLATFORMS.map(({ name, Icon }) => (
                <button
                  key={name}
                  className="w-full flex items-center gap-4 px-10 cursor-pointer hover:brightness-95 transition-all"
                  style={{
                    height: 69, borderRadius: 17,
                    background: '#ffffff', border: '2.8px solid #3973bf',
                  }}
                >
                  <Icon size={26} color="#2b2b2b" />
                  <span style={{ ...F, fontSize: 20, fontWeight: 500, color: '#2b2b2b' }}>{name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherSmartBoard;
